<template>
  <LSection :use-margin="false">
    <template #body>
      <div class="aboutChatbot space-y-8">
        <div class="heading1">
          {{ title }}
          <hr class="gradientLine" />
        </div>

        <section class="chatbot-box">
          <h2 class="box-subtitle">{{ whatItIsSubtitle }}</h2>
          <div class="text-gray-700">
            <div class="gap-6 h8:grid-cols-2 grid">
              <p v-for="(paragraph, index) in descriptionParagraphs" :key="index">
                {{ paragraph }}
              </p>
            </div>
            <ul class="mt-8 grid h8:grid-cols-3 gap-6">
              <li
                v-for="feature in features"
                :key="feature.title"
                class="feature-card"
              >
                <span class="gradientText font-bold block mb-2">{{
                  feature.title
                }}</span>
                {{ feature.text }}<router-link
                  v-if="feature.link"
                  :to="feature.link.to"
                  class="gradientText font-semibold underline hover:opacity-80"
                >{{ feature.link.label }}</router-link>{{ feature.textSuffix }}
              </li>
            </ul>
          </div>
        </section>

        <section class="chatbot-box">
          <h2 class="box-subtitle">{{ audienceSubtitle }}</h2>
          <ul class="grid h8:grid-cols-3 gap-6 text-gray-700">
            <li
              v-for="item in audienceItems"
              :key="item.title"
              class="feature-card"
            >
              <span class="gradientText font-bold block mb-2">{{
                item.title
              }}</span>
              {{ item.text }}
            </li>
          </ul>
        </section>

        <section v-if="resources.length" class="chatbot-box">
          <h2 class="box-subtitle">{{ resourcesSubtitle }}</h2>
          <ul class="space-y-3 text-gray-700">
            <li v-for="resource in resources" :key="resource.label">
              <router-link
                v-if="resource.to"
                :to="resource.to"
                class="gradientText font-semibold underline hover:opacity-80"
              >
                {{ resource.label }}
              </router-link>
              <a
                v-else
                :href="resource.href"
                target="_blank"
                rel="noopener noreferrer"
                class="gradientText font-semibold underline hover:opacity-80"
              >
                {{ resource.label }}
              </a>
              <span v-if="resource.description" class="block mt-1 text-sm">
                {{ resource.description }}
              </span>
            </li>
          </ul>
        </section>

        <section class="chatbot-box">
          <h2 class="box-subtitle">{{ tryItSubtitle }}</h2>
          <video v-if="video" controls class="w-full rounded-lg">
            <source :src="video.src" :type="video.type" />
            Your browser does not support the video tag.
          </video>
          <template v-if="embed">
            <p v-if="embedIntro" class="text-gray-700 mb-4">{{ embedIntro }}</p>
            <iframe
              :src="embed.src"
              frameborder="0"
              class="w-full min-h-[700px] rounded-lg border border-gray-200"
              :title="embed.title"
            ></iframe>
            <p class="mt-4 text-gray-700">
              If the embed does not load,
              <a
                :href="embed.src"
                target="_blank"
                rel="noopener noreferrer"
                class="gradientText font-semibold underline"
              >
                open it in a new tab
              </a>
              .
            </p>
          </template>
          <div v-else-if="ctaRouteName" class="flex flex-col items-center">
            <router-link
              :to="{ name: ctaRouteName }"
              class="text-xl bg-yellow-400 rounded-lg w-fit h-fit m-8 font-extrabold px-2 hover:underline"
            >
              {{ ctaText }}
            </router-link>
          </div>
        </section>
      </div>
    </template>
  </LSection>
</template>

<script>
import LSection from "@/components/layout/LSection.vue";
import { onMounted } from "vue";

export default {
  name: "ChatbotAboutLayout",
  components: {
    LSection,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    whatItIsSubtitle: {
      type: String,
      default: "What it does",
    },
    descriptionParagraphs: {
      type: Array,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
    audienceSubtitle: {
      type: String,
      default: "Who it's for & when to use it",
    },
    audienceItems: {
      type: Array,
      required: true,
    },
    resourcesSubtitle: {
      type: String,
      default: "Further reading",
    },
    resources: {
      type: Array,
      default: () => [],
    },
    tryItSubtitle: {
      type: String,
      default: "See it in action",
    },
    video: {
      type: Object,
      default: null,
    },
    embed: {
      type: Object,
      default: null,
    },
    embedIntro: {
      type: String,
      default: "",
    },
    ctaText: {
      type: String,
      default: "",
    },
    ctaRouteName: {
      type: String,
      default: "",
    },
  },
  setup() {
    const setHeadingColor = () => {
      const heading = document.getElementById("analysisHeader");

      if (heading !== null && heading?.classList) {
        heading.classList.add("text-white");
        heading.classList.remove("text-black");
      }
    };

    onMounted(() => {
      window.addEventListener("scroll", setHeadingColor);
    });
  },
};
</script>
